import type { NexperienceAvailableDatesFilterInput } from '@data-access/graphql/schema.types'
import { execute } from '@data-access/lib/execute'
import {
  type NexperienceAvailableAndAlternativeDatesQueryVariables,
  type NexperienceAvailableDatesQueryVariables,
  NexperienceTimeslotsDocument,
  type NexperienceTimeslotsQuery,
  type NexperienceTimeslotsQueryVariables,
} from '@data-access/lib/gql/experience/documents'
import { getGraphqlQueryKey } from '@data-access/lib/graphql-query'
import { isNotNil, sum } from 'es-toolkit'

import { serializeExperienceInterval } from './experience-date-range'
import type { ExperienceFilter, SelectedOptions } from './experience-state-types'

export const EXPERIENCE_AVAILABILITY_STALE_TIME = 15_000
type ExperienceTimeslotsPageParam = {
  skip: number
  take: number
}

export const EXPERIENCE_TIMESLOTS_INITIAL_PAGE_PARAM: ExperienceTimeslotsPageParam = {
  skip: 0,
  take: 3,
}
const EXPERIENCE_TIMESLOTS_PREVIEW_PAGE_PARAM: ExperienceTimeslotsPageParam = {
  skip: 0,
  take: 5,
}

type ExperienceAvailabilityInput = {
  publicId: number
  previewToken?: string
}

type ExperienceAvailableDatesInput = ExperienceAvailabilityInput & {
  selectedVariant?: string | null
  selectedOptions?: SelectedOptions | null
}

export type ExperienceTimeslotsInput = ExperienceAvailabilityInput & {
  filter: ExperienceFilter
}

export function getExperienceAvailableDatesVariables({
  publicId,
  previewToken,
  selectedVariant,
  selectedOptions,
}: ExperienceAvailableDatesInput): NexperienceAvailableDatesQueryVariables {
  return {
    publicId,
    previewToken,
    variantId: selectedVariant ?? '',
    filter: mapSelectedOptionsToFilter(selectedOptions),
  }
}

export function getExperienceTimeslotsPreviewVariables({
  publicId,
  previewToken,
  filter,
}: ExperienceTimeslotsInput): NexperienceAvailableAndAlternativeDatesQueryVariables {
  return {
    publicId,
    previewToken,
    variantId: filter.selectedVariant ?? '',
    filter: mapExperienceFilterToTimeslotsFilter(filter),
    pagination: EXPERIENCE_TIMESLOTS_PREVIEW_PAGE_PARAM,
  }
}

export function getExperienceTimeslotsBaseVariables({
  publicId,
  previewToken,
  filter,
}: ExperienceTimeslotsInput): Omit<NexperienceTimeslotsQueryVariables, 'pagination'> {
  return {
    publicId,
    previewToken,
    variantId: filter.selectedVariant ?? '',
    filter: mapExperienceFilterToTimeslotsFilter(filter),
  }
}

export function getExperienceTimeslotsQueryKey(input: ExperienceTimeslotsInput) {
  return getGraphqlQueryKey(
    NexperienceTimeslotsDocument,
    getExperienceTimeslotsBaseVariables(input)
  )
}

export function getNextExperienceTimeslotsPageParam(
  page: NexperienceTimeslotsQuery,
  pages: NexperienceTimeslotsQuery[]
) {
  const timeslotsCountPerDate = pages.flatMap(currentPage =>
    currentPage.nexperienceAvailableDates.dates.map(date => date.timeslots.length)
  )

  if (sum(timeslotsCountPerDate) >= page.nexperienceAvailableDates.count) {
    return undefined
  }

  return {
    skip: pages.length * EXPERIENCE_TIMESLOTS_INITIAL_PAGE_PARAM.take,
    take: EXPERIENCE_TIMESLOTS_INITIAL_PAGE_PARAM.take,
  }
}

export function fetchExperienceTimeslotsPageClient(variables: NexperienceTimeslotsQueryVariables) {
  return execute(NexperienceTimeslotsDocument, variables)
}

function mapSelectedOptionsToFilter(
  selectedOptions?: SelectedOptions | null
): NexperienceAvailableDatesFilterInput | undefined {
  const options = mapSelectedOptions(selectedOptions)

  if (!options) {
    return undefined
  }

  return { options }
}

function mapExperienceFilterToTimeslotsFilter(
  filter: ExperienceFilter
): NexperienceAvailableDatesFilterInput | undefined {
  const options = mapSelectedOptions(filter.selectedOptions)

  if (!options && !filter.from && !filter.to) {
    return undefined
  }

  return {
    options,
    ...serializeExperienceInterval(filter),
  }
}

function mapSelectedOptions(selectedOptions?: SelectedOptions | null) {
  const options = Object.entries(selectedOptions ?? {})
    .map(([optionId, count]) => ({ optionId, count }))
    .filter(isNotNil)

  return options.length > 0 ? options : undefined
}
